import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CalendarDays, Plus, ChevronLeft, ChevronRight, LayoutGrid, Clock, Timer } from "lucide-react";
import { toast } from "sonner";
import { format, addDays, subDays } from "date-fns";
import { DispatchStats } from "@/components/dispatch/DispatchStats";
import { CrewList } from "@/components/dispatch/CrewList";
import { UnassignedJobs } from "@/components/dispatch/UnassignedJobs";
import { AssignmentCard } from "@/components/dispatch/AssignmentCard";
import { DayTimeline } from "@/components/dispatch/DayTimeline";
import { TimeClockLiveBoard } from "@/components/timeclock/TimeClockLiveBoard";
import { TimeClockPunchCard } from "@/components/timeclock/TimeClockPunchCard";

export default function DispatchBoard() {
  const { orgId } = useAuth();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [assignments, setAssignments] = useState<any[]>([]);
  const [crews, setCrews] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Form state
  const [selectedJobId, setSelectedJobId] = useState("");
  const [selectedCrewId, setSelectedCrewId] = useState("");
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("17:00");

  useEffect(() => {
    if (orgId) {
      loadData();
    }
  }, [orgId, selectedDate]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load assignments for selected date
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from("crew_assignments")
        .select(`
          *,
          crew:crews(*),
          job:jobs(*)
        `)
        .eq("org_id", orgId)
        .eq("scheduled_date", dateStr)
        .order("start_time");

      if (assignmentsError) throw assignmentsError;
      setAssignments(assignmentsData || []);

      // Load crews
      const { data: crewsData, error: crewsError } = await supabase
        .from("crews")
        .select("*")
        .eq("org_id", orgId)
        .eq("status", "available")
        .order("name");

      if (crewsError) throw crewsError;
      setCrews(crewsData || []);

      // Load active jobs
      const { data: jobsData, error: jobsError } = await supabase
        .from("jobs")
        .select("id, job_number, property_address, status")
        .eq("org_id", orgId)
        .in("status", ["in_progress", "scheduled", "new"])
        .order("job_number");

      if (jobsError) throw jobsError;
      setJobs(jobsData || []);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load dispatch board");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssignment = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const { error } = await supabase.from("crew_assignments").insert({
        job_id: selectedJobId,
        crew_id: selectedCrewId,
        org_id: orgId,
        scheduled_date: format(selectedDate, "yyyy-MM-dd"),
        start_time: startTime,
        end_time: endTime,
        status: "scheduled",
      });

      if (error) throw error;

      toast.success("Crew assigned to job");
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Error creating assignment:", error);
      toast.error("Failed to assign crew");
    }
  };

  const resetForm = () => {
    setSelectedJobId("");
    setSelectedCrewId("");
    setStartTime("08:00");
    setEndTime("17:00");
  };

  const handleStatusUpdate = async (assignmentId: string, newStatus: string) => {
    try {
      const updates: any = { status: newStatus };
      
      if (newStatus === "in_progress") {
        updates.check_in_time = new Date().toISOString();
      } else if (newStatus === "completed") {
        updates.check_out_time = new Date().toISOString();
      }

      const { error } = await supabase
        .from("crew_assignments")
        .update(updates)
        .eq("id", assignmentId);

      if (error) throw error;

      toast.success("Status updated");
      loadData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "scheduled":
        return "default";
      case "in_progress":
        return "secondary";
      case "completed":
        return "outline";
      case "cancelled":
        return "destructive";
      default:
        return "default";
    }
  };

  const CREW_COLORS = [
    "border-primary",
    "border-secondary",
    "border-accent",
    "border-warning",
    "border-success",
  ];

  const completedToday = assignments.filter(a => a.status === "completed").length;
  const inProgressCount = assignments.filter(a => a.status === "in_progress").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading dispatch board...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <CalendarDays className="w-8 h-8 text-primary" />
                <div>
                  <h1 className="text-2xl font-bold">Dispatch Board</h1>
                  <p className="text-sm text-muted-foreground">Production scheduling command center</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Date Navigation */}
              <div className="flex items-center gap-2 bg-muted/50 rounded-lg p-1">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedDate(subDays(selectedDate, 1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="px-3 text-sm font-medium min-w-[140px] text-center">
                  {format(selectedDate, "MMM d, yyyy")}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedDate(addDays(selectedDate, 1))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={resetForm} className="gap-2">
                    <Plus className="w-4 h-4" />
                    Assign Crew
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Assign Crew to Job</DialogTitle>
                    <DialogDescription>
                      Schedule a crew for {format(selectedDate, "MMMM d, yyyy")}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateAssignment} className="space-y-4">
                    <div>
                      <Label>Job</Label>
                      <Select value={selectedJobId} onValueChange={setSelectedJobId} required>
                        <SelectTrigger>
                          <SelectValue placeholder="Select job" />
                        </SelectTrigger>
                        <SelectContent>
                          {jobs.filter(job => job.id).map((job) => (
                            <SelectItem key={job.id} value={job.id}>
                              {job.job_number} - {job.property_address}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Crew</Label>
                      <Select value={selectedCrewId} onValueChange={setSelectedCrewId} required>
                        <SelectTrigger>
                          <SelectValue placeholder="Select crew" />
                        </SelectTrigger>
                        <SelectContent>
                          {crews.filter(crew => crew.id).map((crew) => (
                            <SelectItem key={crew.id} value={crew.id}>
                              {crew.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Start Time</Label>
                        <input
                          type="time"
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          required
                        />
                      </div>
                      <div>
                        <Label>End Time</Label>
                        <input
                          type="time"
                          value={endTime}
                          onChange={(e) => setEndTime(e.target.value)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          required
                        />
                      </div>
                    </div>

                    <Button type="submit" className="w-full">
                      Create Assignment
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="mt-4">
            <DispatchStats
              availableCrews={crews.length}
              scheduledJobs={assignments.length}
              inProgressCount={inProgressCount}
              completedToday={completedToday}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Calendar */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="w-5 h-5" />
                  Calendar
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-md border w-full"
                />
              </CardContent>
            </Card>

            {/* Available Crews */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Available Crews</CardTitle>
              </CardHeader>
              <CardContent>
                <CrewList crews={crews} />
              </CardContent>
            </Card>

            {/* Unassigned Jobs */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Unassigned Jobs</CardTitle>
              </CardHeader>
              <CardContent>
                <UnassignedJobs 
                  jobs={jobs} 
                  onAssignClick={(jobId) => {
                    setSelectedJobId(jobId);
                    setDialogOpen(true);
                  }}
                />
              </CardContent>
            </Card>
          </div>

          {/* Main Area */}
          <div className="space-y-6">
            <Tabs defaultValue="list" className="w-full">
              <TabsList className="grid w-full max-w-[600px] grid-cols-3">
                <TabsTrigger value="list" className="gap-2">
                  <LayoutGrid className="w-4 h-4" />
                  List View
                </TabsTrigger>
                <TabsTrigger value="timeline" className="gap-2">
                  <Clock className="w-4 h-4" />
                  Timeline
                </TabsTrigger>
                <TabsTrigger value="timeclock" className="gap-2">
                  <Timer className="w-4 h-4" />
                  Time Clock
                </TabsTrigger>
              </TabsList>

              <TabsContent value="list" className="space-y-4 mt-6">
                {assignments.length === 0 ? (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center py-16">
                      <CalendarDays className="w-16 h-16 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No assignments scheduled</h3>
                      <p className="text-sm text-muted-foreground text-center mb-4">
                        Click "Assign Crew" to schedule work for {format(selectedDate, "MMMM d, yyyy")}
                      </p>
                      {crews.length === 0 && (
                        <p className="text-xs text-warning">
                          ⚠️ No crews available. Create crews in Crew Management first.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {assignments.map((assignment, index) => (
                      <AssignmentCard
                        key={assignment.id}
                        assignment={assignment}
                        crewColor={CREW_COLORS[index % CREW_COLORS.length]}
                        onStatusUpdate={handleStatusUpdate}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="timeline" className="mt-6">
                <DayTimeline
                  assignments={assignments}
                  crews={crews}
                  selectedDate={selectedDate}
                />
              </TabsContent>

              <TabsContent value="timeclock" className="mt-6 space-y-4">
                <TimeClockPunchCard source="dispatch" />
                <TimeClockLiveBoard />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
